import React, { useState, useEffect, FormEvent, ChangeEvent, useCallback, useRef } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import { getTimeZoneLabel } from "../../../shared/utils/timeZone";
import { getSliderMarkIndices, selectedSliderMarkSx, standardSliderSx } from "../../../shared/styles/sliderStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { Mark } from "@mui/material/Slider/useSlider.types";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ResourceContextPanel from "../../../shared/ui/ResourceContextPanel";
import ConfirmActionDialog from "../../../shared/ui/ConfirmActionDialog";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  formatActivityChangeLines,
  formatActivityScheduleChangeLines,
  formatActivityValue,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import formatTimeTo12Hour from "../../../shared/utils/formatTimeTo12Hour";
import formatTimeTo24Hour from "../../../shared/utils/formatTimeTo24Hour";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

// ── Type Definitions ──────────────────────────────────────────────────────────

interface ServerConfig {
  rowKey: string;
  serverName: string;
  subscriptionId: string;
  resourceGroup: string;
  mySqlServerName: string;
  vmScaleSetName?: string; // Keep for backward compatibility
  isScaleEnabled: boolean;
  downscaleTimeString: string;
  downscaleComputeSize: number;
  upscaleTimeString: string;
  upscaleComputeSize: number;
  weekendComputeSize: number;
  currentComputeSize?: number | null; // Current compute size from Azure
  currentSku?: string | null; // Current SKU from Azure
  currentStatus?: string; // Current status: Running, Stopped, Unknown
  lastUpscaleRun?: string;
  lastDownscaleRun?: string;
}

interface ActivityLogEntry {
  id: string;
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  completedAtUtc?: string | null;
  durationMs?: number | null;
  changeSummary: string;
  changesJson?: string;
  errorMessage?: string;
  pipelineRunUrl?: string;
}

interface ExecutionEntry {
  id: string;
  executionId: string;
  actionType: string;
  executionType: string;
  triggerType: string;
  status: string;
  statusMessage?: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  runningAtUtc?: string | null;
  updatedAtUtc?: string;
  completedAtUtc?: string;
  durationMs?: number | null;
  pipelineRunUrl?: string;
  errorMessage?: string;
  metadataJson?: string;
}

const getAzurePortalMySqlServerUrl = (subscriptionId?: string, resourceGroup?: string, serverName?: string) => {
  if (!subscriptionId || !resourceGroup || !serverName) {
    return "";
  }

  return [
    "https://portal.azure.com/#resource",
    `subscriptions/${subscriptionId}`,
    `resourceGroups/${encodeURIComponent(resourceGroup)}`,
    "providers/Microsoft.DBforMySQL",
    `flexibleServers/${encodeURIComponent(serverName)}`,
    "overview",
  ].join("/");
};

// Business Critical tier vCores: 2, 4, 8, 16, 32, 64, 96
const ComputeSizeValues = [0, 2, 4, 8, 16, 32, 64, 96];

// ── Main Component: DbServerScaling ─────────────────────────────────────────────

const DbServerScaling = () => {
  // ── Constants & UI Configuration
  // Create evenly spaced marks
  const normalizedMarks = ComputeSizeValues.map((value, index) => ({
    value: (index / (ComputeSizeValues.length - 1)) * 100,
    label: `${value}`,
  } as Mark));

  const normalizeValue = (value: number) => {
    const index = ComputeSizeValues.indexOf(value);
    return index >= 0 ? (index / (ComputeSizeValues.length - 1)) * 100 : 0;
  };

  const denormalizeValue = (value: number) => {
    const step = 100 / (ComputeSizeValues.length - 1);
    const index = Math.round(value / step);
    return ComputeSizeValues[index];
  };

  // ── State Management
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading();
  const apiUrl = `${process.env.REACT_APP_API_URL}/api/xuno/server-scaling`;

  const [servers, setServers] = useState<ServerConfig[]>([]);
  const [selectedServer, setSelectedServer] = useState<string>("XunoA");
  // Fetches check this to drop late responses for the previous server
  const selectedServerRef = useRef(selectedServer);
  selectedServerRef.current = selectedServer;
  const [formData, setFormData] = useState({
    isScaleEnabled: true,
    downscaleTime: "22:00", // 10:00 PM
    downscaleComputeSize: 4,
    upscaleTime: "06:30", // 6:30 AM
    upscaleComputeSize: 8,
    weekendComputeSize: 4,
  });

  const [realTimeComputeSize, setRealTimeComputeSize] = useState<number | null>(null);
  const [showBackupScheduleModal, setShowBackupScheduleModal] = useState(false);
  const [backupJobs, setBackupJobs] = useState<any[]>([]);
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  // Refs, not state, so typing does not recreate fetchData and reset the inputs
  const isScheduleDirtyRef = useRef(false);
  const isRealtimeDirtyRef = useRef(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const [stopConfirm, setStopConfirm] = useState<"resize" | "turn_off" | null>(null);

  const fetchBackupJobs = async () => {
    try {
      const accessToken = await getAccessToken();

      const jobsApiUrl = `${process.env.REACT_APP_API_URL}/api/xuno/jobs`;
      const response = await axios.get(jobsApiUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setBackupJobs(response.data || []);
    } catch (error) {
      console.error("Error fetching backup jobs:", error);
      setBackupJobs([]);
    }
  };

  // ── API Functions: Data Fetching
  const fetchData = useCallback(async (showLoadingState = true, showErrorToast = true, syncInputs = true) => {
    try {
      const accessToken = await getAccessToken();
      if (showLoadingState) {
        setLoading(true);
      }

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (selectedServer !== selectedServerRef.current) return;

      const serverConfigs = response.data as ServerConfig[];
      setServers(serverConfigs);

      const resolvedSelection =
        serverConfigs.some((serverConfig) => serverConfig.rowKey === selectedServer)
          ? selectedServer
          : serverConfigs[0]?.rowKey ?? "";

      if (resolvedSelection !== selectedServer) {
        setSelectedServer(resolvedSelection);
      }

      // Load configuration for selected server
      const currentServer = serverConfigs.find((s) => s.rowKey === resolvedSelection);
      if (currentServer) {
        // Handle backward compatibility - check both old and new property names
        const downscaleSize = (currentServer as any).downscaleComputeSize ?? (currentServer as any).downscaleInstanceCount ?? 4;
        const upscaleSize = (currentServer as any).upscaleComputeSize ?? (currentServer as any).upscaleInstanceCount ?? 8;
        const weekendSize = (currentServer as any).weekendComputeSize ?? 4;

        // Get current compute size from Azure (handle both PascalCase and camelCase)
        // API returns camelCase (currentComputeSize) - check both for compatibility
        // Important: nullish coalescing (??) only falls back for null/undefined, NOT for 0
        // If currentComputeSize is 0, it means server is stopped, which is a valid value
        const rawCurrentSize = (currentServer as any).currentComputeSize ?? (currentServer as any).CurrentComputeSize;

        // Keep null when Azure size is unknown or not on the slider, a schedule target here would be misleading
        // 0 is still valid (server is stopped)
        const currentSize = typeof rawCurrentSize === "number" && ComputeSizeValues.includes(rawCurrentSize) ? rawCurrentSize : null;

        // Update currentServer object to have consistent property names
        if (currentServer) {
          (currentServer as any).currentComputeSize = rawCurrentSize; // Store the raw value (can be 0)
          (currentServer as any).currentSku = (currentServer as any).currentSku ?? (currentServer as any).CurrentSku ?? null;
          (currentServer as any).currentStatus = (currentServer as any).currentStatus ?? (currentServer as any).CurrentStatus ?? "Unknown";
        }

        if (syncInputs || !isScheduleDirtyRef.current) {
          setFormData({
            isScaleEnabled: currentServer.isScaleEnabled,
            downscaleTime: formatTimeTo24Hour(currentServer.downscaleTimeString),
            downscaleComputeSize: downscaleSize,
            upscaleTime: formatTimeTo24Hour(currentServer.upscaleTimeString),
            upscaleComputeSize: upscaleSize,
            weekendComputeSize: weekendSize,
          });
          isScheduleDirtyRef.current = false;
        }

        if (syncInputs || !isRealtimeDirtyRef.current) {
          setRealTimeComputeSize(currentSize);
          isRealtimeDirtyRef.current = false;
        }
      }
    } catch (ex) {
      if (showErrorToast && selectedServer === selectedServerRef.current) {
        toast.error("Failed to load server settings.");
      }
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  }, [apiUrl, selectedServer, setLoading]);

  const getExecutionChangeLines = (log: ActivityFeedEntry) => {
    const lines: string[] = [];

    const parsed = parseActivityDetailsJson<any>(log.detailsJson);
    const previousComputeSize = parsed?.previousComputeSize ?? parsed?.computeSize?.before;
    const requestedComputeSize = parsed?.requestedComputeSize ?? parsed?.computeSize?.after;
    const sizeChanged = previousComputeSize !== undefined
      && previousComputeSize !== null
      && requestedComputeSize !== undefined
      && hasActivityValueChanged(previousComputeSize, requestedComputeSize);
    const skuChanged = Boolean(parsed?.previousSku
      && parsed?.requestedSku
      && hasActivityValueChanged(parsed.previousSku, parsed.requestedSku));

    if (sizeChanged) {
      const previousSize = `${formatActivityValue(previousComputeSize)} vCores`;
      const requestedSize = `${formatActivityValue(requestedComputeSize)} vCores`;
      const previousSku = skuChanged ? ` (${formatActivityValue(parsed.previousSku)})` : "";
      const requestedSku = skuChanged ? ` (${formatActivityValue(parsed.requestedSku)})` : "";
      lines.push(`Compute Size: ${previousSize}${previousSku} -> ${requestedSize}${requestedSku}`);
    }

    if (!sizeChanged && skuChanged) {
      lines.push(`SKU: ${formatActivityValue(parsed.previousSku)} -> ${formatActivityValue(parsed.requestedSku)}`);
    }

    return lines.slice(0, 12);
  };

  const getAuditChangeLines = (changesJson?: string) => {
    return [
      ...formatActivityScheduleChangeLines(changesJson),
      ...formatActivityChangeLines(changesJson, {
        isScaleEnabled: "Scaling Enabled",
        downscaleTime: "Downscale Time",
        downscaleComputeSize: "Downscale Compute Size",
        upscaleTime: "Upscale Time",
        upscaleComputeSize: "Upscale Compute Size",
        weekendComputeSize: "Weekend Compute Size",
      }),
    ].slice(0, 12);
  };

  const buildActivityFeed = (
    executionItems: ExecutionEntry[],
    auditItems: ActivityLogEntry[]
  ): ActivityFeedEntry[] => {
    const executionFeed = mapExecutionFeedEntries(executionItems);

    const auditFeed: ActivityFeedEntry[] = auditItems
      .filter((log) => log.actionType === "scheduled_compute_settings")
      .map((log) => ({
        id: log.id,
        source: "audit" as const,
        actionType: log.actionType,
        status: log.status,
        actorName: log.actorName,
        actorEmail: log.actorEmail,
        startedAtUtc: log.startedAtUtc,
        durationMs: log.durationMs,
        detailsJson: log.changesJson,
        errorMessage: log.errorMessage,
      }));

    return sortActivityFeedEntries([...executionFeed, ...auditFeed]);
  };

  const fetchActivityLogs = useCallback(async (serverKey: string, page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const accessToken = await getAccessToken();
      const [auditItems, executionItems] = await Promise.all([
        axios.get<PaginatedResponse<ActivityLogEntry>>(`${apiUrl}/${serverKey}/activity-logs`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
        axios.get<PaginatedResponse<ExecutionEntry>>(`${apiUrl}/${serverKey}/executions`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
      ]);
      if (serverKey !== selectedServerRef.current) return;
      const mergedFeed = buildActivityFeed(executionItems, auditItems);
      const totalCount = mergedFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = mergedFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      setHasActiveExecutions(executionItems.some((item) => isExecutionActive(item.status)));
    } catch {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState || serverKey !== selectedServerRef.current) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl]);

  // ── Event Handlers: Form & User Actions
  const handleSaveSettings = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const accessToken = await getAccessToken();

      await axios.put(
        `${apiUrl}/${selectedServer}/settings`,
        {
          isScaleEnabled: formData.isScaleEnabled,
          upscaleTime: formatTimeTo12Hour(formData.upscaleTime),
          downscaleTime: formatTimeTo12Hour(formData.downscaleTime),
          upscaleComputeSize: formData.upscaleComputeSize,
          downscaleComputeSize: formData.downscaleComputeSize,
          weekendComputeSize: formData.weekendComputeSize,
          actionType: "scheduled_compute_settings",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      toast.success("Schedule applied.");
      isScheduleDirtyRef.current = false;
      void fetchData(false, false);
      void fetchActivityLogs(selectedServer, 1, false, false);
    } catch (error: any) {
      console.error("Error saving settings:", error);
      toast.error(getApiErrorMessage(error, "Unable to apply schedule."));
    } finally {
      setLoading(false);
    }
  };

  const handleRealTimeUpdate = async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();

      const response = await axios.post(
        `${apiUrl}/${selectedServer}/update`,
        {
          computeSize: realTimeComputeSize,
          actionType: "realtime_compute_size",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      toast.success(response.data?.message ?? "Resize started. Activity Log will update.");
      setHasActiveExecutions(true);
      isRealtimeDirtyRef.current = false;
      void fetchActivityLogs(selectedServer, 1, false, false);
      void fetchData(false, false);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to resize server."));
    } finally {
      setLoading(false);
    }
  };

  const handleResizeSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (realTimeComputeSize === null) {
      toast.error("Current DB size unavailable. Refresh before resizing.");
      return;
    }
    // Size 0 stops the prod server, confirm first
    if (realTimeComputeSize === 0) {
      setStopConfirm("resize");
      return;
    }
    void handleRealTimeUpdate();
  };

  const handleStopConfirm = () => {
    const action = stopConfirm;
    setStopConfirm(null);
    void (action === "turn_off" ? handleTurnOff() : handleRealTimeUpdate());
  };

  const handleTurnOn = async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();

      // Use upscale compute size from form data, or default to 8 vCores
      const turnOnComputeSize = formData.upscaleComputeSize > 0 ? formData.upscaleComputeSize : 8;

      const response = await axios.post(
        `${apiUrl}/${selectedServer}/update`,
        {
          computeSize: turnOnComputeSize,
          actionType: "turn_on",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      toast.success(response.data?.message ?? "Turn on started. Activity Log will update.");
      setHasActiveExecutions(true);
      isRealtimeDirtyRef.current = false;
      void fetchActivityLogs(selectedServer, 1, false, false);
      void fetchData(false, false);
    } catch (error: any) {
      console.error("Error turning on server:", error);
      toast.error(getApiErrorMessage(error, "Failed to turn on server."));
    } finally {
      setLoading(false);
    }
  };

  const handleTurnOff = async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();

      const response = await axios.post(
        `${apiUrl}/${selectedServer}/update`,
        {
          computeSize: 0,
          actionType: "turn_off",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      toast.success(response.data?.message ?? "Turn off started. Activity Log will update.");
      setHasActiveExecutions(true);
      isRealtimeDirtyRef.current = false;
      void fetchActivityLogs(selectedServer, 1, false, false);
      void fetchData(false, false);
    } catch (error: any) {
      console.error("Error turning off server:", error);
      toast.error(getApiErrorMessage(error, "Failed to turn off server."));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    isScheduleDirtyRef.current = true;
  };

  function getValueText(value: number) {
    const vCores = denormalizeValue(value);
    return vCores === 0 ? "0 (Off)" : `${vCores} vCore${vCores > 1 ? 's' : ''}`;
  }

  // ── Effects: Data Fetching & Polling
  useEffect(() => {
    if (instance && accounts.length > 0) {
      void fetchData();
      void fetchActivityLogs(selectedServer, 1);
    }
  }, [instance, accounts, selectedServer, fetchData, fetchActivityLogs]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchData(false, false, false);
      void fetchActivityLogs(selectedServer, activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, fetchData, hasActiveExecutions, selectedServer]);

  const currentServer = servers.find((s) => s.rowKey === selectedServer);
  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText:
      log.actionType === "scheduled_compute_settings"
        ? "Apply Schedule"
        : log.actionType === "turn_on"
          ? "Turn On"
          : log.actionType === "turn_off"
            ? "Turn Off"
            : "Apply Resize",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: log.source === "execution" ? getExecutionChangeLines(log) : getAuditChangeLines(log.detailsJson),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.pipelineRunUrl,
  }));

  // API can send PascalCase too, check both
  const currentComputeSize = currentServer?.currentComputeSize ?? (currentServer as any)?.CurrentComputeSize;
  const currentSku = currentServer?.currentSku ?? (currentServer as any)?.CurrentSku;
  const currentStatus = currentServer ? currentServer.currentStatus ?? (currentServer as any).CurrentStatus ?? "Unknown" : undefined;
  const currentSizeText = currentComputeSize === 0
    ? "0 vCores (Off)"
    : currentComputeSize ? `${currentComputeSize} vCores` : "";
  const selectedResourceName = currentServer
    ? `${currentServer.mySqlServerName} (${currentServer.serverName.replace(/^Xuno\s+/i, "")})`
    : undefined;

  // Map selectedServer (XunoA, XunoB, etc.) to serverId format (A, B, etc.)
  const serverIdMap: { [key: string]: string } = {
    'XunoA': 'A',
    'XunoB': 'B',
    'XunoD': 'D',
    'XunoE': 'E'
  };
  const mappedServerId = serverIdMap[selectedServer] || selectedServer.replace('Xuno', '');

  // Try both formats: mapped (A, B, D, E) and original (XunoA, XunoB, etc.)
  const serverBackupJobs = backupJobs.filter((job: any) => {
    const jobServerId = job.serverId?.toUpperCase() || '';
    return jobServerId === mappedServerId.toUpperCase() ||
           jobServerId === selectedServer.toUpperCase() ||
           jobServerId === selectedServer.replace('Xuno', '').toUpperCase();
  });

  // There might be multiple jobs per server
  const backupTimes: string[] = serverBackupJobs
    .filter((job: any) => job.isEnabled && job.scheduleTime)
    .map((job: any) => job.scheduleTime);

  // ── Render: UI Layout
  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Sets scheduled or immediate compute changes for the selected Xuno MySQL flexible server.">
          <h3 className="text-sm font-semibold text-slate-800">Server compute</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Weekday and weekend schedules set a compute size. Size 0 turns the server off.</li>
            <li>Real-Time Resize applies the selected size immediately.</li>
            <li>Turn On starts the server at the weekday upscale size. Turn Off stops it.</li>
            <li>Turn on, turn off, and resize take around 3 minutes.</li>
            <li>Execution: Azure Resource Manager SDK.</li>
            <li>Only server compute changes. Database data stays unchanged.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && <div className="mt-2" id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>

      <ResourceContextPanel
        resourceName={selectedResourceName || currentServer?.vmScaleSetName}
        status={currentStatus}
        currentSize={currentSizeText}
        currentSizeValueLabel=""
        currentSizeDetailLabel="SKU"
        currentSizeDetail={currentComputeSize > 0 && currentSku ? currentSku : undefined}
        portalUrl={currentServer ? getAzurePortalMySqlServerUrl(currentServer.subscriptionId, currentServer.resourceGroup, currentServer.mySqlServerName) : null}
      />

      <section className="workspace-card mt-2 overflow-hidden border border-slate-200 bg-white">
      {/* Resource selector */}
      <div className="border-b border-slate-200 px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Database Server</h3>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">Select the database server to manage.</p>
        <div className="mt-4">
          <label htmlFor="selectedServer" className="sr-only">Database Server</label>
          <select
            id="selectedServer"
            value={selectedServer}
            onChange={(e) => setSelectedServer(e.target.value)}
            className="block w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
          >
            {servers.length === 0 ? (
              <option value="">No database server found</option>
            ) : (
              servers.map((server) => (
                <option key={server.rowKey} value={server.rowKey}>
                  {server.serverName}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Automatic Scaling Configuration */}
      <form onSubmit={handleSaveSettings} className="border-b border-slate-200 px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Schedule Automation</h3>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">
          Set automatic weekday and weekend scaling.
        </p>
        <hr className="my-3 border-slate-200" />

        {/* Skip while the list is still empty, default key is set before data loads */}
        {servers.length > 0 && !currentServer && (
          <div className="mb-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            No settings found for this server.
          </div>
        )}

        <label className="mb-3 flex cursor-pointer select-none items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs font-semibold text-slate-700" htmlFor="isScaleEnabled">
            <span>Enable Schedule</span>
            <input
              name="isScaleEnabled"
              id="isScaleEnabled"
              type="checkbox"
              checked={formData.isScaleEnabled}
              onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                const isChecked = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  isScaleEnabled: isChecked,
                }));
                isScheduleDirtyRef.current = true;

                // Show backup schedule modal when enabling schedule
                if (isChecked) {
                  await fetchBackupJobs();
                  setShowBackupScheduleModal(true);
                }
              }}
              className="form-checkbox"
            />
        </label>

        <p className="mt-3 mb-2 text-xs font-semibold text-slate-700">{`Weekday Schedule (${getTimeZoneLabel()})`}</p>
        <div className="mb-3 space-y-3">
          {/* Upscale fields */}
          <div className="rounded-sm border border-slate-200 bg-white p-3">
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[132px,minmax(0,1fr)]">
              <div>
                <label className="block text-xs font-semibold text-gray-700" htmlFor="upscaleTime">
                  Upscale Window
                </label>
                <input
                  type="time"
                  step={60}
                  aria-label="Upscale start time"
                  name="upscaleTime"
                  id="upscaleTime"
                  value={formData.upscaleTime}
                  onChange={handleChange}
                  disabled={!formData.isScaleEnabled}
                  className="mt-2 w-full"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="upscaleComputeSize">
                  Compute Size (vCores)
                </label>
                <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(formData.upscaleComputeSize))) }}>
                  <Slider
                    id="upscaleComputeSize"
                    name="upscaleComputeSize"
                    value={normalizeValue(formData.upscaleComputeSize)}
                    getAriaValueText={getValueText}
                    valueLabelFormat={getValueText}
                    step={null}
                    valueLabelDisplay="auto"
                    marks={normalizedMarks}
                    onChange={(_: Event, newValue: number | number[]) => {
                      setFormData((prev) => ({
                        ...prev,
                        upscaleComputeSize: denormalizeValue(newValue as number)
                      }));
                      isScheduleDirtyRef.current = true;
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>

        {/* Downscale fields */}
        <div className="rounded-sm border border-slate-200 bg-white p-3">
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[132px,minmax(0,1fr)]">
            <div>
              <label htmlFor="downscaleTime" className="block text-xs font-semibold text-gray-700">Downscale Window</label>
              <input
                name="downscaleTime"
                id="downscaleTime"
                type="time"
                step={60}
                aria-label="Downscale start time"
                value={formData.downscaleTime}
                onChange={handleChange}
                disabled={!formData.isScaleEnabled}
                className="mt-2 w-full"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="downscaleComputeSize">
                Compute Size (vCores)
              </label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(formData.downscaleComputeSize))) }}>
                <Slider
                  id="downscaleComputeSize"
                  name="downscaleComputeSize"
                  aria-label="Downscale Compute Size"
                  step={null}
                  value={normalizeValue(formData.downscaleComputeSize)}
                  getAriaValueText={getValueText}
                  valueLabelFormat={getValueText}
                  valueLabelDisplay="auto"
                  marks={normalizedMarks}
                  onChange={(_: Event, newValue: number | number[]) => {
                    setFormData((prev) => ({
                      ...prev,
                      downscaleComputeSize: denormalizeValue(newValue as number)
                    }));
                    isScheduleDirtyRef.current = true;
                  }}
                />
              </Box>
            </div>
          </div>
        </div>

        {/* Weekend Settings */}
        <p className="mt-4 mb-2 text-xs font-semibold text-slate-700">Weekend Schedule</p>
        <div className="mb-3 rounded-sm border border-slate-200 bg-white p-3">
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[132px,minmax(0,1fr)]">
            <div>
              <p className="text-xs font-semibold text-gray-700">Weekend Target</p>
              <p className="mt-1 text-[10px] leading-4 text-gray-500">Starts at 00:00 on Saturday and ends with Monday's weekday schedule.</p>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-700 mb-1" htmlFor="weekendComputeSize">
                Compute Size (vCores)
              </label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(formData.weekendComputeSize))) }}>
                <Slider
                  id="weekendComputeSize"
                  name="weekendComputeSize"
                  aria-label="Weekend Compute Size"
                  step={null}
                  value={normalizeValue(formData.weekendComputeSize)}
                  getAriaValueText={getValueText}
                  valueLabelFormat={getValueText}
                  valueLabelDisplay="auto"
                  marks={normalizedMarks}
                  onChange={(_: Event, newValue: number | number[]) => {
                    setFormData((prev) => ({
                      ...prev,
                      weekendComputeSize: denormalizeValue(newValue as number)
                    }));
                    isScheduleDirtyRef.current = true;
                  }}
                />
              </Box>
            </div>
          </div>
        </div>

        <div className="mt-3 flex justify-start">
        <button type="submit" className={btn.applySmall}>
          Apply Schedule
        </button>
        </div>
      </form>

      {/* Real-Time Scaling */}
      <form onSubmit={handleResizeSubmit} className="px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Manual Action</h3>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">Resize, turn on, or turn off the server now.</p>
        <hr className="my-3 border-slate-200" />

        {currentServer && !ComputeSizeValues.includes(currentComputeSize) && (
          <p className="mb-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-800">
            Current Azure size unavailable. Refresh to continue.
          </p>
        )}

        <div className="rounded-sm border border-slate-200 bg-white p-3">
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[132px,minmax(0,1fr)]">
            <div>
              <label className="block text-xs font-semibold text-gray-700">Real-Time Resize</label>
            </div>
            <div className="min-w-0">
              <label htmlFor="realTimeComputeSize" className="block text-xs font-semibold text-gray-700 mb-1">Compute Size (vCores)</label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(realTimeComputeSize ?? ComputeSizeValues[0]))) }}>
                <Slider
                  id="realTimeComputeSize"
                  aria-label="Compute Size"
                  value={normalizeValue(realTimeComputeSize ?? ComputeSizeValues[0])}
                  disabled={realTimeComputeSize === null}
                  step={null}
                  getAriaValueText={getValueText}
                  valueLabelFormat={getValueText}
                  valueLabelDisplay="auto"
                  marks={normalizedMarks}
                  onChange={(_: Event, newValue: number | number[]) => {
                    setRealTimeComputeSize(denormalizeValue(newValue as number));
                    isRealtimeDirtyRef.current = true;
                  }}
                />
              </Box>
              {realTimeComputeSize === 0 && (
                <p className="mt-1 text-[11px] font-medium leading-4 text-red-600">0 stops the server.</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 flex justify-start gap-3">
          <button
            type="submit"
            className={btn.applySmall}
            disabled={realTimeComputeSize === null}
          >
            Apply Resize
          </button>
          <button type="button" onClick={() => void handleTurnOn()} className={btn.positiveSmall}>
            Turn On
          </button>
          <button type="button" onClick={() => setStopConfirm("turn_off")} className={btn.destructiveSmall}>
            Turn Off
          </button>
        </div>
      </form>
      </section>

      <ActivityLogTable
        title="Activity Log"
        description="Tracks scaling requests and schedule changes."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(selectedServer, activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(selectedServer, pageNumber)}
      />
      </div>}

      <ConfirmActionDialog
        open={stopConfirm !== null}
        title={stopConfirm === "turn_off" ? "Confirm Turn Off" : "Confirm Resize"}
        description="This stops the database server until it is turned on again."
        confirmLabel={stopConfirm === "turn_off" ? "Yes, turn off" : "Yes, apply resize"}
        destructive
        onConfirm={handleStopConfirm}
        onCancel={() => setStopConfirm(null)}
      >
        <div className="rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
          <p className="font-medium text-gray-800">Server to stop</p>
          <p className="mt-2 text-xs font-bold text-blue-600">{selectedResourceName || selectedServer}</p>
        </div>
      </ConfirmActionDialog>

      {/* Info only, Continue and Escape just close and keep the toggle as set */}
      <ConfirmActionDialog
        open={showBackupScheduleModal}
        title="DB Backup Schedule Information"
        description="Review the backup window before enabling automatic scaling."
        confirmLabel="Continue"
        hideCancel
        onConfirm={() => setShowBackupScheduleModal(false)}
        onCancel={() => setShowBackupScheduleModal(false)}
      >
        {serverBackupJobs.length === 0 ? (
          <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-2">
            <p className="text-sm text-yellow-800">
              No enabled backup schedule found for <span className="font-semibold">{selectedServer}</span>. You can continue.
            </p>
          </div>
        ) : backupTimes.length === 0 ? (
          <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-2">
            <p className="text-sm text-yellow-800">
              Backup schedule for <span className="font-semibold">{selectedServer}</span> is disabled. You can continue.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-sm border border-blue-200 bg-blue-50 px-3 py-2">
              <p className="text-sm font-medium text-gray-800">
                Backup window for <span className="font-semibold">{selectedServer}</span>:
              </p>
              <div className="mt-2 space-y-1">
                {backupTimes.map((time, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
                    <p className="text-sm font-semibold text-blue-800">
                      Backup time: <span className="text-blue-900">{time}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-sm border border-orange-200 bg-orange-50 px-3 py-2">
              <p className="text-sm text-orange-800">
                <span className="font-semibold">Important:</span> Set downscale at least 1 hour after backup.
              </p>
            </div>
          </div>
        )}
      </ConfirmActionDialog>
    </div>
  );
};

export default DbServerScaling;
